import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  User,
  CreditCard,
  QrCode,
  Bell,
  LogOut,
  ChevronRight,
  Shield,
  Heart,
  FileText,
  Pencil,
  Lock,
  X,
  Check,
  Eye,
  EyeOff,
  Coins,
  CalendarClock,
  Loader2
} from 'lucide-react'
import { exchangesApi, authApi, pointsApi, settingsApi, checkinApi, hospitalsApi } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { usePageTitle } from '../../hooks/usePageTitle'

// 模块级锁：刷新窗口内只请求一次系统设置
let _sysSettingsLoaded = false

const CROWD_TYPES = ['慢性病患者', '慢性病高危人群', '普通人群']
const AGE_GROUPS = ['>65', '<=65']

/** 格式化承诺书签署时间：2026-05-15 10:30 */
function formatSignedDate(s: string): string {
  if (!s) return ''
  const d = new Date(s)
  if (isNaN(d.getTime())) return s
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const UserProfile = () => {
  usePageTitle()
  const navigate = useNavigate()
  const { logout, syncUser } = useAuth()

  const [loading, setLoading] = useState(true)
  const [userAccount, setUserAccount] = useState<any>(null)

  // 更换手机号相关
  const [showPhoneModal, setShowPhoneModal] = useState(false)
  const [newPhone, setNewPhone] = useState('')
  const [code, setCode] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [step, setStep] = useState(1)

  // 修改密码相关
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // 承诺书
  const [showCommitment, setShowCommitment] = useState(false)
  const [commitmentSigned, setCommitmentSigned] = useState(true)
  const [commitmentContent, setCommitmentContent] = useState('')
  const [commitmentAgreed, setCommitmentAgreed] = useState(false)
  const [commitmentLoading, setCommitmentLoading] = useState(false)

  // 小调查
  const [surveyEnabled, setSurveyEnabled] = useState(false)
  const [surveyQrUrl, setSurveyQrUrl] = useState('')
  const [surveyAppName, setSurveyAppName] = useState('')
  const [showSurveyModal, setShowSurveyModal] = useState(false)

  // 签到
  const [checkinEnabled, setCheckinEnabled] = useState(false)
  const [checkinPoints, setCheckinPoints] = useState(10)
  const [checkedIn, setCheckedIn] = useState(false)
  const [checkinLoading, setCheckinLoading] = useState(false)
  const [checkinMsg, setCheckinMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null)
  const [frozenModalVisible, setFrozenModalVisible] = useState(false)
  const [frozenMessage, setFrozenMessage] = useState('')

  // 通用 toast
  const [toast, setToast] = useState<{ text: string; type: 'success' | 'error' } | null>(null)
  const showToast = (text: string, type: 'success' | 'error' = 'success') => {
    setToast({ text, type })
    setTimeout(() => setToast(null), 2200)
  }

  // 补全健康信息弹窗
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileValidated, setProfileValidated] = useState(false) // 点击保存后才显示验证错误
  const [hospitals, setHospitals] = useState<{ id: string; name: string }[]>([])
  const [profileFormData, setProfileFormData] = useState({
    hospital_id: '',
    crowd_type: '',
    age_group: '',
  })

  // 身份证号脱敏：显示前6位 + **** + 后4位
  const maskIdCard = (idCard: string) => {
    if (!idCard || idCard.length < 10) return idCard || ''
    return idCard.slice(0, 6) + '********' + idCard.slice(-4)
  }

  // 加载用户信息
  useEffect(() => {
    window.scrollTo(0, 0)
    const loadUser = async () => {
      // 如果 URL 有 token，优先同步到 AuthProvider 并写入 localStorage（扫码/登录后首次访问）
      const params = new URLSearchParams(window.location.search)
      const tokenFromUrl = params.get('token')
      if (tokenFromUrl) {
        try {
          const parts = tokenFromUrl.split('.')
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
            const authUser = {
              token: tokenFromUrl,
              userId: payload.sub,
              name: '',
              role: 'user' as const,
            }
            localStorage.setItem('auth_user', JSON.stringify(authUser))
            localStorage.setItem('token', tokenFromUrl)
            syncUser(authUser)
          }
        } catch (e) {
          console.error('解析token失败', e)
        }
      }

      const resp = await authApi.refreshUserInfo() as any
      if (resp) {
        // 检查账号是否被冻结
        if (resp.status === 'frozen') {
          setFrozenMessage('账号已被冻结，请联系管理员')
          setFrozenModalVisible(true)
        }
        setUserAccount(resp)
      } else {
        // 降级：从 localStorage 读取
        setUserAccount(JSON.parse(localStorage.getItem('userAccount') || 'null'))
      }

      // 加载系统设置到 localStorage（同步锁，API 返回前就拦住重复调用）
      if (_sysSettingsLoaded) return
      _sysSettingsLoaded = true
      try {
        const sys = await settingsApi.getSystem()
        if (sys) {
          if (sys.systemName) localStorage.setItem('systemName', sys.systemName)
          if (sys.systemTitle) localStorage.setItem('systemTitle', sys.systemTitle)
          if (sys.copyright) localStorage.setItem('copyright', sys.copyright)
          if (sys.systemLogo) localStorage.setItem('systemLogo', sys.systemLogo)
        }
      } catch {
        _sysSettingsLoaded = false  // 失败则解锁，允许重试
      }

      setLoading(false)
    }
    loadUser()
  }, [])

  // 加载签到状态
  useEffect(() => {
    const loadCheckinStatus = async () => {
      try {
        const status = await checkinApi.status() as any
        setCheckinEnabled(status.enabled)
        setCheckinPoints(status.points || 10)
        setCheckedIn(status.checked_in)
      } catch (e: any) {
        // 账号被冻结
        if (e?.message?.includes('冻结') || e?.detail?.includes('冻结')) {
          setFrozenMessage(e?.detail || e?.message || '账号已被冻结，请联系管理员')
          setFrozenModalVisible(true)
        }
        console.error('Failed to load checkin status', e)
      }
    }
    loadCheckinStatus()
  }, [])

  // 页面加载时预先加载机构列表（用于机构名称展示）
  useEffect(() => {
    ;(async () => {
      try {
        const hospResp: any = await hospitalsApi.list({ is_active: true })
        const hospList = (hospResp.hospitals || hospResp || []).map((h: any) => ({ id: h.id, name: h.name }))
        setHospitals(hospList)
        // 保存到本地缓存
        import('../../lib/api').then(({ saveHospitalToCache, updateUserAccountHospitalName }) => {
          saveHospitalToCache(hospList)
          updateUserAccountHospitalName()
        })
      } catch (hospErr) {
        console.error('[Profile] 加载机构列表失败:', hospErr)
      }
    })()
  }, [])

  // 检查健康信息是否需要补全（userAccount 有值后执行一次）
  useEffect(() => {
    if (!userAccount) return
    const needProfile = !userAccount.hospitalId || !userAccount.crowdType || !userAccount.ageGroup
    if (!needProfile) return
    setProfileFormData({
      hospital_id: userAccount.hospitalId || '',
      crowd_type: userAccount.crowdType || '',
      age_group: userAccount.ageGroup || '',
    })
    ;(async () => {
      try {
        const hospResp: any = await hospitalsApi.list({ is_active: true })
        const hospList = (hospResp.hospitals || hospResp || []).map((h: any) => ({ id: h.id, name: h.name }))
        setHospitals(hospList)
        // 保存到本地缓存
        import('../../lib/api').then(({ saveHospitalToCache, updateUserAccountHospitalName }) => {
          saveHospitalToCache(hospList)
          updateUserAccountHospitalName()
        })
      } catch (hospErr) {
        console.error('[Profile] 加载机构列表失败:', hospErr)
      }
      setShowProfileModal(true)
    })()
  }, [userAccount])

  // 承诺书
  const openCommitmentModal = async () => {
    setShowCommitment(true)
    setCommitmentAgreed(false)
    setCommitmentLoading(true)
    try {
      const res: any = await settingsApi.getCommitment()
      setCommitmentContent(res?.content || '（暂无承诺书内容，请联系管理员配置）')
      setCommitmentSigned(!!userAccount?.commitmentSignedAt)
    } catch (e) {
      setCommitmentContent('（加载失败，请稍后重试）')
    } finally {
      setCommitmentLoading(false)
    }
  }

  const handleSignCommitment = async () => {
    if (!commitmentAgreed || commitmentLoading) return
    setCommitmentLoading(true)
    try {
      const res: any = await settingsApi.signCommitment()
      const signedAt = res?.signed_at || new Date().toISOString()
      const updated = { ...(userAccount || {}), commitmentSignedAt: signedAt }
      setUserAccount(updated)
      localStorage.setItem('userAccount', JSON.stringify(updated))
      setCommitmentSigned(true)
      setShowCommitment(false)
      showToast('承诺书签署成功', 'success')
    } catch (e: any) {
      showToast(e?.response?.data?.detail || '签署失败，请稍后重试', 'error')
    } finally {
      setCommitmentLoading(false)
    }
  }

  // 加载小调查配置
  useEffect(() => {
    settingsApi.getSurvey().then((res: any) => {
      if (res) {
        setSurveyEnabled(!!res.enabled)
        setSurveyQrUrl(res.qrCodeUrl || '')
        setSurveyAppName(res.appName || '')
      }
    }).catch(() => { /* 静默失败 */ })
  }, [])

  // 签到
  const handleCheckin = async () => {
    if (checkinLoading || checkedIn) return
    setCheckinLoading(true)
    setCheckinMsg(null)
    try {
      const result = await checkinApi.checkin() as any
      setCheckinMsg({ text: result.message || '签到成功！', type: 'success' })
      setCheckedIn(true)
      // 刷新用户积分
      const resp = await authApi.refreshUserInfo()
      if (resp) setUserAccount(resp)
      // 3秒后自动隐藏提示
      setTimeout(() => setCheckinMsg(null), 3000)
    } catch (e: any) {
      setCheckinMsg({ text: e.message || '签到失败', type: 'error' })
      setTimeout(() => setCheckinMsg(null), 3000)
    } finally {
      setCheckinLoading(false)
    }
  }

  const userData = userAccount || { id: '', name: '', phone: '', points: 0, role: 'user' }

  // 检测用户身份（统一从 userAccount 读取，refreshUserInfo 已写入 role/medicalRole）
  // - 普通用户：role = 'user'，medicalRole = undefined
  // - 普通医护（staff）：role = 'medical'，medicalRole = 'staff'
  // - 机构负责人（supervisor）：role = 'medical'，medicalRole = 'supervisor'
  const isMedical = userAccount?.role === 'medical'
  const isSupervisor = userAccount?.medicalRole === 'supervisor'

  const handleLogout = () => {
    logout()
    navigate('/user/login', { replace: true })
  }

  const sendCode = () => {
    if (!newPhone) {
      alert('请输入新手机号')
      return
    }
    setCountdown(60)
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleChangePhone = () => {
    if (step === 1) {
      if (!newPhone) {
        alert('请输入新手机号')
        return
      }
      setStep(2)
    } else if (step === 2) {
      if (!code) {
        alert('请输入验证码')
        return
      }
      alert('手机号更换成功！')
      setShowPhoneModal(false)
      setStep(1)
      setNewPhone('')
      setCode('')
    }
  }

  const handleChangePassword = () => {
    if (!password) {
      alert('请输入当前密码')
      return
    }
    if (!newPassword || newPassword.length < 6) {
      alert('新密码至少6位')
      return
    }
    if (newPassword !== confirmPassword) {
      alert('两次密码不一致')
      return
    }
    alert('密码修改成功！')
    setShowPasswordModal(false)
    setPassword('')
    setNewPassword('')
    setConfirmPassword('')
  }

  // 旧占位 handleSignCommitment 已删除，逻辑由上方 async 版本实现

  const menuItems = [
    {
      icon: QrCode,
      title: '我的身份码',
      subtitle: '扫码享受健康服务',
      color: 'bg-green-100 text-green-600',
      link: '/user/scan'
    },
    {
      icon: Bell,
      title: '消息通知',
      subtitle: '查看系统通知和活动',
      color: 'bg-orange-100 text-orange-600',
      link: '/user/notifications',
      badge: userAccount?.unreadCount > 0 ? userAccount.unreadCount : 0
    },
    {
      icon: User,
      title: '个人资料',
      subtitle: '编辑个人信息和头像',
      color: 'bg-blue-100 text-blue-600',
      link: '/user/personal-info'
    },
    {
      icon: Shield,
      title: '账号安全',
      subtitle: '修改密码、绑定手机',
      color: 'bg-purple-100 text-purple-600',
      link: '/user/account-security'
    },
    {
      icon: FileText,
      title: '承诺书',
      subtitle: userAccount?.commitmentSignedAt
        ? `已于 ${formatSignedDate(userAccount.commitmentSignedAt)} 签署`
        : '请阅读并签署健康生活承诺书',
      color: 'bg-amber-100 text-amber-600',
      action: 'commitment'
    }
  ]

  return (
    <div className="bg-gray-50 pb-4">
      {/* 用户信息卡片 */}
      <div className="bg-gradient-to-br from-green-600 to-green-700 px-4 pt-6 pb-8">
        <div className="flex items-center space-x-4">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg overflow-hidden">
            <img
              src={userData.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userData.name}`}
              alt={userData.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1 text-white">
            <h2 className="text-xl font-bold">{userData.name}</h2>
            <p className="text-green-100 text-sm mt-1 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              {userData.phone || '暂无手机号'}
            </p>
            <p className="text-green-200 text-xs mt-0.5">会员ID：{userData.memberNo || '暂无'}</p>
          </div>
          <Link
            to="/user/personal-info"
            className="p-2 bg-white/20 rounded-lg"
          >
            <User className="w-5 h-5 text-white" />
          </Link>
        </div>

        {/* 机构（单独一行） */}
        <div className="mt-3 flex items-center text-green-100 text-xs">
          <Shield className="w-3 h-3 mr-1 opacity-70" />
          <span>
            {userAccount?.hospitalId
              ? (hospitals.find(h => h.id === userAccount.hospitalId)?.name || userAccount.hospitalId)
              : '未绑定机构'}
          </span>
        </div>

        {/* 人群类型 + 年龄范围（同一行） */}
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
          <div className="flex items-center text-green-100 text-xs">
            <Heart className="w-3 h-3 mr-1 opacity-70" />
            <span>{userAccount?.crowdType || '未填'}</span>
            <button
              onClick={() => {
                setProfileFormData({
                  hospital_id: userAccount?.hospitalId || '',
                  crowd_type: userAccount?.crowdType || '',
                  age_group: userAccount?.ageGroup || '',
                })
                ;(async () => {
                  try {
                    const hospResp: any = await hospitalsApi.list({ is_active: true })
                    setHospitals((hospResp.hospitals || hospResp || []).map((h: any) => ({ id: h.id, name: h.name })))
                  } catch {}
                  setShowProfileModal(true)
                })()
              }}
              className="ml-1 p-0.5 hover:bg-white/20 rounded transition-colors"
            >
              <Pencil className="w-3 h-3" />
            </button>
          </div>
          <div className="flex items-center text-green-100 text-xs">
            <CalendarClock className="w-3 h-3 mr-1 opacity-70" />
            <span>
              {userAccount?.ageGroup
                ? userAccount.ageGroup === '>65' ? '65岁以上' : '65岁及以下'
                : '未填'}
            </span>
            <button
              onClick={() => {
                setProfileFormData({
                  hospital_id: userAccount?.hospitalId || '',
                  crowd_type: userAccount?.crowdType || '',
                  age_group: userAccount?.ageGroup || '',
                })
                ;(async () => {
                  try {
                    const hospResp: any = await hospitalsApi.list({ is_active: true })
                    setHospitals((hospResp.hospitals || hospResp || []).map((h: any) => ({ id: h.id, name: h.name })))
                  } catch {}
                  setShowProfileModal(true)
                })()
              }}
              className="ml-1 p-0.5 hover:bg-white/20 rounded transition-colors"
            >
              <Pencil className="w-3 h-3" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          <Link
            to="/user/points"
            className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center block"
          >
            <span className="text-white font-bold text-lg block">{userData.points ?? 0}</span>
            <span className="text-green-100 text-xs">我的积分</span>
          </Link>
          <Link
            to="/user/exchange-records"
            className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center hover:bg-white/30 transition-colors block"
          >
            <span className="text-white font-bold text-lg block">{userAccount?.exchangeCount || 0}</span>
            <span className="text-green-100 text-xs">兑换记录</span>
          </Link>
          <Link to="/user/points" className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center hover:bg-white/30 transition-colors block">
            <span className="text-white font-bold text-lg block">{userAccount?.eventCount || 0}</span>
            <span className="text-green-100 text-xs">参与活动</span>
          </Link>
        </div>

        {/* 每日签到按钮 */}
        {checkinEnabled && (
          <div className="mt-4">
            <button
              onClick={handleCheckin}
              disabled={checkedIn || checkinLoading}
              className={`w-full py-3 rounded-xl font-medium flex items-center justify-center space-x-2 transition-all ${
                checkedIn
                  ? 'bg-white/20 text-green-100 cursor-not-allowed'
                  : 'bg-white text-green-700 hover:bg-green-50 shadow-lg'
              }`}
            >
              {checkinLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Coins className="w-5 h-5" />
                  <span>{checkedIn ? '今日已签到' : `每日签到 +${checkinPoints}积分`}</span>
                </>
              )}
            </button>
            {checkinMsg && (
              <div
                className={`mt-2 py-2 px-3 rounded-lg text-sm text-center ${
                  checkinMsg.type === 'success'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {checkinMsg.text}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 身份信息 */}
      <div className="px-4 -mt-4">
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-green-600" />
            </div>
            <Link to="/user/personal-info" className="flex-1">
              <p className="text-sm text-gray-500">身份证号</p>
              {userData.idCard ? (
                <p className="font-medium text-gray-800">{maskIdCard(userData.idCard)}</p>
              ) : (
                <p className="text-sm text-green-600 hover:underline">暂未填写，点击编辑</p>
              )}
            </Link>
          </div>
        </div>
      </div>

      {/* 医护/机构管理快捷入口（统一卡片，紧凑网格） */}
      {isMedical && (
        <div className="px-4 mt-4">
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {/* 卡片标题栏 */}
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-xs text-gray-400 font-medium">
                {isSupervisor ? '机构管理功能' : '医护管理功能'}
              </p>
            </div>
            {/* 图标网格 */}
            {isSupervisor ? (
              /* 机构负责人 3列（移除账号管理） */
              <div className="grid grid-cols-3 divide-x divide-gray-100">
                {[
                  { to: '/user/point-entry', icon: Coins, color: 'bg-green-50 text-green-600', label: '积分录入' },
                  { to: '/user/events', icon: CalendarClock, color: 'bg-teal-50 text-teal-600', label: '事件记录' },
                  { to: '/user/verify', icon: FileText, color: 'bg-amber-50 text-amber-600', label: '待审核' },
                ].map(({ to, icon: Icon, color, label }) => (
                  <Link key={to} to={to} className="flex flex-col items-center p-4 hover:bg-gray-50 transition-colors">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-2 ${color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-xs text-gray-700">{label}</span>
                  </Link>
                ))}
              </div>
            ) : (
              /* 普通医护 2列 */
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                {[
                  { to: '/user/point-entry', icon: Coins, color: 'bg-green-50 text-green-600', label: '积分录入', sub: '手动 + 扫码' },
                  { to: '/user/events', icon: CalendarClock, color: 'bg-teal-50 text-teal-600', label: '事件记录', sub: '查看所有事件' },
                ].map(({ to, icon: Icon, color, label, sub }) => (
                  <Link key={to} to={to} className="flex items-center p-4 hover:bg-gray-50 transition-colors">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mr-3 flex-shrink-0 ${color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{label}</p>
                      <p className="text-xs text-gray-400">{sub}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 功能菜单 */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-xl shadow-md divide-y divide-gray-100">
          {menuItems.map((item, index) => {
            const Icon = item.icon
            const inner = (
              <>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 relative ${item.color}`}>
                  <Icon className="w-5 h-5" />
                  {item.badge && item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{item.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{item.subtitle}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </>
            )
            if (item.action === 'commitment') {
              return (
                <div
                  key={index}
                  className="flex items-center p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={openCommitmentModal}
                >
                  {inner}
                </div>
              )
            }
            return (
              <Link
                key={index}
                to={item.link || '#'}
                className="flex items-center p-4 hover:bg-gray-50 transition-colors"
              >
                {inner}
              </Link>
            )
          })}
        </div>
      </div>

      {/* 退出登录 */}
      <div className="px-4 mt-4">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center space-x-2 py-4 bg-white rounded-xl shadow-md text-red-600 hover:bg-red-50 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">退出登录</span>
        </button>
      </div>

      {/* 版权信息 */}
      <div className="px-4 mt-4 pb-4 text-center">
        <p className="text-xs text-gray-400">{localStorage.getItem('copyright') || '© 2026 眉山疾控'}</p>
      </div>

      {/* 承诺书弹窗 */}
      {showCommitment && (
        <div
          className="fixed inset-0 z-50 pb-20 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowCommitment(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="bg-white w-full max-w-md max-h-[90vh] flex flex-col shadow-[0_-8px_30px_rgba(0,0,0,0.18)] sm:shadow-2xl sm:rounded-2xl rounded-t-3xl overflow-hidden animate-slide-up sm:animate-none"
          >
            {/* 顶部装饰条（拖动指示 + 绿色强调） */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            {/* 头部 */}
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-2">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(22, 163, 74, 0.12)' }}>
                  <FileText className="w-5 h-5" style={{ color: 'rgb(22, 163, 74)' }} />
                </div>
                <h3 className="text-base font-bold text-gray-800">健康生活承诺书</h3>
              </div>
              <button
                onClick={() => setShowCommitment(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* 内容滚动区 */}
            <div className="flex-1 overflow-y-auto px-6 py-5 text-[15px] text-gray-700 leading-7 whitespace-pre-wrap">
              {commitmentLoading ? (
                <div className="flex items-center justify-center py-12 text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  加载中...
                </div>
              ) : (
                commitmentContent
              )}
            </div>

            {/* 底部操作区 */}
            <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0 bg-gradient-to-b from-white to-gray-50" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
              {commitmentSigned ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center text-sm text-gray-600 bg-white border border-green-100 rounded-xl py-2.5">
                    <Check className="w-4 h-4 mr-1.5" style={{ color: 'rgb(22, 163, 74)' }} />
                    已签署 · <span className="ml-1 font-medium" style={{ color: 'rgb(22, 163, 74)' }}>{formatSignedDate(userAccount?.commitmentSignedAt)}</span>
                  </div>
                  <button
                    onClick={() => setShowCommitment(false)}
                    className="w-full py-3 rounded-xl font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    关闭
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="flex items-start space-x-2 text-sm text-gray-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={commitmentAgreed}
                      onChange={e => setCommitmentAgreed(e.target.checked)}
                      className="w-4 h-4 mt-0.5 accent-green-600"
                    />
                    <span>我已阅读并同意以上承诺书全部内容</span>
                  </label>
                  <button
                    onClick={handleSignCommitment}
                    disabled={!commitmentAgreed || commitmentLoading}
                    className="w-full py-3.5 rounded-xl font-medium text-white transition-all shadow-md disabled:bg-gray-300 disabled:shadow-none disabled:cursor-not-allowed"
                    style={{ backgroundColor: (!commitmentAgreed || commitmentLoading) ? undefined : 'rgb(22, 163, 74)' }}
                  >
                    {commitmentLoading ? (
                      <span className="inline-flex items-center justify-center">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        签署中...
                      </span>
                    ) : (
                      '签署承诺书'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 更换手机号弹窗 */}
      {showPhoneModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">更换手机号</h3>
                <p className="text-green-100 text-sm">需由医院负责人及以上角色操作</p>
              </div>
              <button
                onClick={() => { setShowPhoneModal(false); setStep(1); setNewPhone(''); setCode('') }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {step === 1 && (
                <>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>注意：</strong>更换手机号后，原手机号将无法登录。新手机号需确保未被其他账号绑定。
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      新手机号
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        placeholder="请输入新手机号"
                        value={newPhone}
                        onChange={(e) => setNewPhone(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-500">
                      更换手机号申请提交后，将由医院负责人进行审核确认，审核通过后手机号将自动更新。
                    </p>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <Check className="w-12 h-12 text-green-600 mx-auto mb-2" />
                    <p className="text-gray-700">验证码已发送至新手机号</p>
                    <p className="text-green-600 font-medium mt-1">{newPhone}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      验证码
                    </label>
                    <div className="flex space-x-3">
                      <input
                        type="text"
                        placeholder="请输入验证码"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        className="flex-1 px-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <button
                        onClick={sendCode}
                        disabled={countdown > 0}
                        className={`px-4 py-4 rounded-xl font-medium ${
                          countdown > 0
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-green-700 text-white hover:bg-green-800'
                        }`}
                      >
                        {countdown > 0 ? `${countdown}秒` : '获取验证码'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="p-6 border-t flex space-x-3">
              <button
                onClick={() => { setShowPhoneModal(false); setStep(1); setNewPhone(''); setCode('') }}
                className="flex-1 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleChangePhone}
                className="flex-1 py-3 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors"
              >
                {step === 1 ? '下一步' : '确认更换'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 小调查悬浮按钮（左下角） */}
      {surveyEnabled && surveyQrUrl && (
        <button
          onClick={() => setShowSurveyModal(true)}
          className="fixed left-4 z-40 active:scale-95 transition-transform"
          style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' }}
          aria-label="小调查"
        >
          <div className="flex flex-col items-center">
            <div className="relative">
              <div
                className="absolute inset-0 rounded-2xl blur-md animate-quiz-breathe"
                style={{
                  background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                }}
              />
              <div className="w-14 h-14 rounded-2xl bg-white shadow-lg flex items-center justify-center relative quiz-icon-breathe">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 50%, #6366f1 100%)',
                  }}
                >
                  <svg className="w-6 h-6 text-white drop-shadow-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 11h6" />
                    <path d="M9 15h4" />
                    <path d="M14 2v4a1 1 0 0 0 1 1h4" />
                    <path d="M5 22h14a2 2 0 0 0 2-2V7l-5-5H5a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2z" />
                  </svg>
                </div>
                <div className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-gradient-to-br from-red-500 to-pink-500 border-2 border-white shadow" />
              </div>
            </div>
            <span className="mt-1.5 px-2 py-0.5 bg-white/95 backdrop-blur-sm text-gray-700 text-[11px] font-medium rounded-md shadow-md whitespace-nowrap border border-gray-100">
              小调查
            </span>
          </div>
        </button>
      )}

      {/* 小调查弹窗 */}
      {showSurveyModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowSurveyModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="bg-white w-full max-w-md max-h-[90vh] flex flex-col shadow-[0_-8px_30px_rgba(0,0,0,0.18)] sm:shadow-2xl sm:rounded-2xl rounded-t-3xl overflow-hidden animate-slide-up sm:animate-none"
          >
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-2">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(59, 130, 246, 0.12)' }}>
                  <svg className="w-5 h-5" style={{ color: '#3b82f6' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 11h6" /><path d="M9 15h4" /><path d="M14 2v4a1 1 0 0 0 1 1h4" /><path d="M5 22h14a2 2 0 0 0 2-2V7l-5-5H5a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2z" />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-gray-800">小调查</h3>
              </div>
              <button
                onClick={() => setShowSurveyModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="px-6 py-6 text-center overflow-y-auto">
              <div className="bg-gradient-to-br from-cyan-50 via-blue-50 to-indigo-50 rounded-2xl p-4 inline-block mx-auto">
                <div className="bg-white rounded-xl p-3 shadow-sm">
                  <img
                    src={surveyQrUrl}
                    alt="小调查二维码"
                    className="w-48 h-48 object-contain"
                  />
                </div>
                <p
                  className="mt-3 text-sm font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #06b6d4, #3b82f6, #6366f1)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {surveyAppName || '小程序'}
                </p>
              </div>

              <div className="mt-5 flex items-start space-x-2 text-left bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5">
                <svg className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <p className="text-xs text-blue-700">
                  长按二维码识别，点击底部小程序名称进入小程序参与调查
                </p>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
              <button
                onClick={() => setShowSurveyModal(false)}
                className="w-full py-3 rounded-xl font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 健康生活承诺书弹窗（新版本，读取后端数据） */}

      {/* 修改密码弹窗 */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">修改密码</h3>
                <p className="text-green-100 text-sm">保护账号安全</p>
              </div>
              <button
                onClick={() => { setShowPasswordModal(false); setPassword(''); setNewPassword(''); setConfirmPassword('') }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  当前密码
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="请输入当前密码"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  新密码
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="请输入新密码（6-20位）"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  确认新密码
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="请再次输入新密码"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div className="bg-green-50 rounded-xl p-4">
                <p className="text-sm text-green-800">
                  <strong>密码要求：</strong>
                </p>
                <ul className="text-sm text-green-600 mt-2 space-y-1">
                  <li className="flex items-center">
                    <Check className={`w-4 h-4 mr-2 ${newPassword.length >= 6 ? 'text-green-500' : 'text-gray-300'}`} />
                    6-20位字符
                  </li>
                  <li className="flex items-center">
                    <Check className={`w-4 h-4 mr-2 ${/[a-zA-Z]/.test(newPassword) ? 'text-green-500' : 'text-gray-300'}`} />
                    包含字母
                  </li>
                  <li className="flex items-center">
                    <Check className={`w-4 h-4 mr-2 ${/\d/.test(newPassword) ? 'text-green-500' : 'text-gray-300'}`} />
                    包含数字
                  </li>
                </ul>
              </div>
            </div>

            <div className="p-6 border-t flex space-x-3">
              <button
                onClick={() => { setShowPasswordModal(false); setPassword(''); setNewPassword(''); setConfirmPassword('') }}
                className="flex-1 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleChangePassword}
                className="flex-1 py-3 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors"
              >
                保存修改
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 账号冻结弹窗 */}
      {frozenModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">账号已被冻结</h3>
              <p className="text-gray-600 mb-6">{frozenMessage || '请联系管理员处理'}</p>
              <button onClick={() => {
                setFrozenModalVisible(false)
                logout()
              }}
                className="w-full py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700">
                退出登录
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 补全健康信息弹窗 */}
      {showProfileModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-2xl mx-4 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg font-bold text-white">
                {userAccount?.hospitalId ? '修改健康信息' : '补全健康信息'}
              </h3>
              <button
                onClick={() => setShowProfileModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {userAccount?.hospitalId ? (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                  <p className="text-sm text-blue-800">机构已绑定，如需变更请联系管理员。</p>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                  <p className="text-sm text-yellow-800">请补全您的健康信息，以便享受更精准的健康服务。机构绑定后不支持变更。</p>
                </div>
              )}

              {/* 绑定机构（已绑定则禁用） */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  绑定机构 {userAccount?.hospitalId && <span className="text-red-500">（已绑定，不可修改）</span>}
                </label>
                {userAccount?.hospitalId ? (
                  <div className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-100 text-gray-500 flex items-center">
                    <Shield className="w-4 h-4 mr-2 text-gray-400" />
                    {hospitals.find(h => h.id === userAccount.hospitalId)?.name || userAccount.hospitalId}
                    <Lock className="w-3 h-3 ml-auto text-gray-400" />
                  </div>
                ) : (
                  <>
                    <select
                      value={profileFormData.hospital_id}
                      onChange={e => { setProfileFormData({ ...profileFormData, hospital_id: e.target.value }); setProfileValidated(false) }}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                    >
                      <option value="">请选择机构</option>
                      {hospitals.map(h => (
                        <option key={h.id} value={h.id}>{h.name}</option>
                      ))}
                    </select>
                    {profileValidated && !profileFormData.hospital_id && <p className="text-red-500 text-xs mt-1">请选择机构</p>}
                  </>
                )}
              </div>

              {/* 人群类型 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">人群类型 <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 gap-2">
                  {CROWD_TYPES.map(ct => (
                    <button
                      key={ct}
                      type="button"
                      onClick={() => { setProfileFormData({ ...profileFormData, crowd_type: ct }); setProfileValidated(false) }}
                      className={`py-2 px-3 text-sm rounded-xl border-2 transition-colors ${
                        profileFormData.crowd_type === ct
                          ? 'border-green-600 bg-green-50 text-green-700'
                          : 'border-gray-200 text-gray-600 hover:border-green-400'
                      }`}
                    >
                      {ct}
                    </button>
                  ))}
                </div>
                {profileValidated && !profileFormData.crowd_type && <p className="text-red-500 text-xs mt-1">请选择人群类型</p>}
              </div>

              {/* 年龄范围 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">年龄范围 <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 gap-3">
                  {AGE_GROUPS.map(ag => (
                    <button
                      key={ag}
                      type="button"
                      onClick={() => { setProfileFormData({ ...profileFormData, age_group: ag }); setProfileValidated(false) }}
                      className={`py-3 px-4 text-sm rounded-xl border-2 transition-colors flex items-center justify-center gap-2 ${
                        profileFormData.age_group === ag
                          ? 'border-green-600 bg-green-50 text-green-700'
                          : 'border-gray-200 text-gray-600 hover:border-green-400'
                      }`}
                    >
                      {ag === '>65' ? '65岁以上' : '65岁及以下'}
                    </button>
                  ))}
                </div>
                {profileValidated && !profileFormData.age_group && <p className="text-red-500 text-xs mt-1">请选择年龄范围</p>}
              </div>
            </div>

            <div className="p-6 border-t flex-shrink-0">
              {!profileFormData.hospital_id && !userAccount?.hospitalId && (
                <p className="text-xs text-gray-400 text-center mb-2">提交后将绑定您的机构</p>
              )}
              <button
                onClick={async () => {
                  // 验证：机构为必填（仅当未绑定时）
                  const needHospital = !userAccount?.hospitalId
                  if (needHospital && !profileFormData.hospital_id) {
                    setProfileValidated(true)
                    return
                  }
                  if (!profileFormData.crowd_type || !profileFormData.age_group) {
                    setProfileValidated(true)
                    return
                  }
                  setProfileLoading(true)
                  try {
                    await authApi.updateProfile({
                      hospital_id: profileFormData.hospital_id || undefined,
                      crowd_type: profileFormData.crowd_type || undefined,
                      age_group: profileFormData.age_group || undefined,
                    })
                    const updated: any = await authApi.refreshUserInfo()
                    if (updated) setUserAccount(updated)
                    setShowProfileModal(false)
                  } catch (e: any) {
                    alert(e?.message || e?.detail || '更新失败')
                  } finally {
                    setProfileLoading(false)
                  }
                }}
                disabled={profileLoading}
                className={`w-full py-3 rounded-xl font-medium text-white transition-colors ${
                  profileLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-700 hover:bg-green-800'
                }`}
              >
                {profileLoading ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 全局 toast */}
      {toast && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[200] pointer-events-none">
          <div
            className={`flex items-center space-x-2 px-5 py-3 rounded-2xl shadow-2xl text-white text-sm font-medium animate-toast-in ${
              toast.type === 'success' ? 'bg-green-600' : 'bg-red-500'
            }`}
            style={{ minWidth: '180px', maxWidth: '320px' }}
          >
            {toast.type === 'success' ? (
              <Check className="w-4 h-4 flex-shrink-0" />
            ) : (
              <X className="w-4 h-4 flex-shrink-0" />
            )}
            <span className="break-words">{toast.text}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserProfile
